/**
 * Refresco periódico de una pantalla.
 *
 * - `setTimeout` encadenado, no `setInterval`: la próxima vuelta se agenda CUANDO TERMINA la
 *   anterior. Con 3 s y un backend lento, `setInterval` apila pedidos que llegan desordenados y
 *   pintan un número viejo encima de uno nuevo.
 * - Con la pestaña oculta no pide; al volver a la pestaña pide una vez enseguida y sigue.
 * - 3 errores seguidos → se detiene (`detenido: true`) y la pantalla lo dice («Dejamos de
 *   actualizar…» + Reintentar). Seguir pidiendo contra un servidor caído sólo esconde el problema
 *   detrás de números que ya no se mueven. Un 401 persistente cae acá.
 * - `intervaloMs === null` → no agenda nada.
 * - NO llama al montar: la primera carga es de la página (que muestra su skeleton y su error).
 *   El hook sólo refresca.
 *
 * Un error es lo que `fn` LANZA. Como `req()` no lanza con 422, una `fn` que quiera contar un 422
 * como falla tiene que lanzar ella.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export const MAX_ERRORES_POLLING = 3;

export function usePolling(fn: () => Promise<void>, intervaloMs: number | null, deps: unknown[]): { errores: number; detenido: boolean; reintentar: () => void } {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const [errores, setErrores] = useState(0);
  const [detenido, setDetenido] = useState(false);
  const [vuelta, setVuelta] = useState(0);
  // `reintentar` pide enseguida (no espera un intervalo entero después de apretar el botón).
  const inmediato = useRef(false);

  useEffect(() => {
    setErrores(0);
    setDetenido(false);
    if (intervaloMs === null || !(intervaloMs > 0)) return;

    let vivo = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let enCurso = false;
    let fallos = 0;
    let parado = false;

    const agendar = () => {
      if (!vivo || parado) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(tick, intervaloMs);
    };

    const correr = async () => {
      if (!vivo || parado || enCurso) return;
      enCurso = true;
      let ok = true;
      try {
        await fnRef.current();
      } catch {
        ok = false;
      }
      enCurso = false;
      if (!vivo) return;
      if (ok) {
        if (fallos) { fallos = 0; setErrores(0); }
      } else {
        fallos += 1;
        setErrores(fallos);
        if (fallos >= MAX_ERRORES_POLLING) {
          parado = true;
          setDetenido(true);
          return;
        }
      }
      agendar();
    };

    function tick() {
      timer = null;
      if (!vivo || parado) return;
      // Pestaña oculta: no se pide ni se agenda; `visibilitychange` retoma.
      if (typeof document !== "undefined" && document.hidden) return;
      void correr();
    }

    const alCambiarVisibilidad = () => {
      if (!vivo || parado || document.hidden || enCurso) return;
      if (timer) { clearTimeout(timer); timer = null; }
      void correr();
    };
    document.addEventListener("visibilitychange", alCambiarVisibilidad);

    if (inmediato.current) {
      inmediato.current = false;
      void correr();
    } else {
      agendar();
    }

    return () => {
      vivo = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", alCambiarVisibilidad);
    };
    // `deps` es la lista de la pantalla (p. ej. el id): cambiarla reinicia la cadena.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervaloMs, vuelta, ...deps]);

  const reintentar = useCallback(() => {
    inmediato.current = true;
    setVuelta((v) => v + 1);
  }, []);

  return { errores, detenido, reintentar };
}
